import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { commandCenterApi } from '../../utils/api';
import { useConfirm } from '../../utils/useConfirm';
import { formatIsoDayShort } from '../../utils/dateFormat';
import { toast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Switch from '../../components/Switch';
import {
  ADJUSTMENT_TYPES, ADJUSTMENT_TYPE_LABELS, LINE_KIND_ADJUSTMENT, LINE_KINDS, LINE_KIND_LABELS, lineKindLabel,
} from '../../utils/lineKind';
import { LoadState } from './parts';

// Command Center: שורות מסמכים, כללי סיווג (#300). Global rules on a line's
// text: every line saved from now on is classified by them, so nobody has to
// classify the same fee by hand on each document (server/lib/lineKindRules.js).
//
// Designed to be read right to left as a sentence (product-designer pass,
// owner-approved mockup, 20/10/2026): "אם השורה [מכילה v] [טקסט] … then סווג כ
// [מוצר | שירות | לא מוצר]". The rules list leads; the form opens on demand.
// The preview is live and read-only — existing lines never change.

export const MATCH_TYPES = [
  { key: 'contains', label: 'מכילה' },
  { key: 'starts_with', label: 'מתחילה ב־' },
  { key: 'exact', label: 'זהה בדיוק ל־' },
];
const phraseOf = (key) => MATCH_TYPES.find((m) => m.key === key)?.label || '';
const n = (v) => (v ?? 0).toLocaleString('en-US');
const PREVIEW_DEBOUNCE_MS = 500;
const SEARCH_FROM = 8;   // the list gets a search once it outgrows a glance
// What the server needs to accept a rule: two characters in a row that its
// text normalization never rewrites (lib/lineKindRules.js MIN_CORE).
const hasCore = (s) => /[^\s"'׳״“”‘’]{2,}/.test(s);
// The client-side twin of the server's normName, for the duplicate check only.
const normText = (s) => String(s ?? '').toLowerCase()
  .replace(/[״“”]/g, '"').replace(/[׳‘’]/g, "'").replace(/\s+/g, ' ').trim();

function countCopy(count) {
  if (!count) return 'עוד לא סיווג שורות';
  if (count === 1) return 'סיווג שורה אחת';
  return `סיווג ${n(count)} שורות`;
}

function PreviewStrip({ preview }) {
  if (preview.state === 'idle') {
    return <div className="cc-rule-preview cc-muted">הקלידו טקסט כדי לראות אילו שורות תואמות היום</div>;
  }
  if (preview.state === 'error') {
    return <div className="cc-rule-preview cc-muted">הבדיקה לא הצליחה — נסו לשנות את הטקסט</div>;
  }
  const d = preview.data;
  const loading = preview.state === 'loading';
  if (!d) return <div className="cc-rule-preview cc-muted" role="status">בודק…</div>;
  return (
    <div className={`cc-rule-preview${loading ? ' is-loading' : ''}`} role="status">
      {d.matched === 0 ? (
        <div>אין היום שורות שתואמות. הכלל יחול על שורות חדשות.</div>
      ) : (
        <div>
          תואם היום ל־<strong>{d.capped || d.partial ? `${n(d.matched)}+` : n(d.matched)}</strong> שורות
          {' '}({n(d.unclassified)} מהן לא מסווגות). הן לא ישתנו.
        </div>
      )}
      {d.partial && <div className="cc-muted">הבדיקה נעצרה מוקדם — המספר הוא מינימום.</div>}
      {d.samples.length > 0 && (
        <ul className="cc-rule-samples">
          {d.samples.map((s) => (
            <li key={s.description}><bdi>{s.description}</bdi>{s.supplier_name ? <span className="cc-muted"> · {s.supplier_name}</span> : null}</li>
          ))}
        </ul>
      )}
      {loading && <div className="cc-muted">בודק…</div>}
    </div>
  );
}

function RuleForm({ rules, onSaved, onCancel }) {
  const [matchType, setMatchType] = useState('contains');
  const [pattern, setPattern] = useState('');
  const [exceptions, setExceptions] = useState([]);
  const [exInput, setExInput] = useState('');
  const [exOpen, setExOpen] = useState(false);
  const [lineKind, setLineKind] = useState('');
  const [adjType, setAdjType] = useState('');
  const [errors, setErrors] = useState({});
  const [preview, setPreview] = useState({ state: 'idle', data: null });
  const [saving, setSaving] = useState(false);
  const patternRef = useRef(null);
  const exRef = useRef(null);
  const kindRef = useRef(null);
  const previewToken = useRef(0);

  useEffect(() => {
    patternRef.current?.focus();
    patternRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, []);

  const duplicate = useMemo(() => {
    const key = normText(pattern);
    return !!key && rules.some((r) => r.match_type === matchType && normText(r.pattern) === key);
  }, [rules, matchType, pattern]);

  // The exceptions a save would send: the chips plus one typed but not yet
  // added. The preview uses the SAME list — editing that typed text must
  // refresh it, or the screen shows the result of a rule that will never be
  // saved (Codex review, PR #302).
  const pendingException = exInput.replace(/,$/, '').trim();
  const effectiveExceptions = pendingException && !exceptions.includes(pendingException)
    ? [...exceptions, pendingException] : exceptions;
  const exceptionsKey = effectiveExceptions.join('\u0001');

  // Live preview: debounced and read-only. Any change to the text, the match
  // type or the exceptions (typed or added) drops the result on screen and the
  // one in flight: the reply is ignored (previewToken) and the request is
  // aborted, which also stops the server's scan.
  useEffect(() => {
    const token = ++previewToken.current;
    if (!hasCore(pattern)) { setPreview({ state: 'idle', data: null }); return undefined; }
    setPreview({ state: 'loading', data: null });
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await commandCenterApi.previewLineKindRule(
          { match_type: matchType, pattern, exceptions: effectiveExceptions },
          { signal: ctrl.signal },
        );
        if (token === previewToken.current) setPreview({ state: 'ok', data });
      } catch {
        if (token === previewToken.current) setPreview({ state: 'error', data: null });
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => { clearTimeout(t); ctrl.abort(); };
    // exceptionsKey stands for effectiveExceptions, whose identity changes each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pattern, matchType, exceptionsKey]);

  const commitException = (raw = exInput) => {
    const text = raw.replace(/,$/, '').trim();
    if (text && !exceptions.includes(text)) setExceptions((list) => [...list, text]);
    setExInput('');
  };
  const removeException = (e) => {
    setExceptions((list) => list.filter((x) => x !== e));
    if (exceptions.length === 1 && !exInput) setExOpen(false);
  };

  const reset = () => {
    setPattern(''); setExceptions([]); setExInput(''); setExOpen(false);
    setLineKind(''); setAdjType(''); setErrors({});
    patternRef.current?.focus();
  };

  const save = async () => {
    const next = {};
    if (!pattern.trim()) next.pattern = 'כתבו את הטקסט שהשורה צריכה להכיל';
    else if (!hasCore(pattern)) next.pattern = 'הטקסט קצר מדי — לפחות מילה של שתי אותיות';
    else if (duplicate) next.pattern = 'כבר יש כלל כזה';
    if (!lineKind) next.kind = 'בחרו סיווג: מוצר, שירות או לא מוצר';
    setErrors(next);
    if (next.pattern) { patternRef.current?.focus(); return; }
    if (next.kind) { kindRef.current?.querySelector('button')?.focus(); return; }
    // An exception typed but not yet turned into a chip still counts.
    const allExceptions = effectiveExceptions;
    setSaving(true);
    try {
      await commandCenterApi.createLineKindRule({
        match_type: matchType, pattern, exceptions: allExceptions,
        line_kind: lineKind, adjustment_type: lineKind === LINE_KIND_ADJUSTMENT ? adjType : '',
      });
      toast('הכלל נשמר — הוא יחול על שורות חדשות');
      reset();
      onSaved();
    } catch (err) {
      setErrors({ server: err?.response?.data?.error || 'שמירת הכלל נכשלה — אפשר לנסות שוב' });
    } finally {
      setSaving(false);
    }
  };

  const patternError = errors.pattern || (duplicate ? 'כבר יש כלל כזה' : '');

  return (
    <section className="cc-rule-form" aria-label="כלל חדש">
      <div className="cc-rule-sentence">
        <span className="cc-rule-word">אם השורה</span>
        <select className="form-control cc-rule-select" aria-label="סוג התאמה" value={matchType}
          onChange={(e) => setMatchType(e.target.value)}>
          {MATCH_TYPES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
        <input ref={patternRef} className="form-control cc-rule-pattern" aria-label="טקסט הכלל" maxLength={200}
          placeholder="לדוגמה: אגרה" value={pattern} aria-invalid={!!patternError}
          onChange={(e) => { setPattern(e.target.value); setErrors((x) => ({ ...x, pattern: undefined })); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }} />
      </div>
      {patternError && <div className="cc-rule-error" role="alert">{patternError}</div>}

      {!exOpen && exceptions.length === 0 ? (
        <button type="button" className="cc-linkbtn cc-rule-more"
          onClick={() => { setExOpen(true); setTimeout(() => exRef.current?.focus(), 0); }}>
          + חוץ מ־…
        </button>
      ) : (
        <div className="cc-rule-sentence">
          <span className="cc-rule-word">חוץ משורות שמכילות</span>
          {exceptions.map((e) => (
            <span key={e} className="cc-chip">
              <bdi>{e}</bdi>
              <button type="button" className="cc-chip-x" aria-label={`הסר חריג ${e}`} onClick={() => removeException(e)}>
                <Icon name="close" size={11} />
              </button>
            </span>
          ))}
          <input ref={exRef} className="form-control cc-rule-ex-input" aria-label="חריג חדש" maxLength={200}
            placeholder="לדוגמה: עיגול פינות" value={exInput}
            onChange={(e) => {
              const v = e.target.value;
              if (v.endsWith(',')) commitException(v); else setExInput(v);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitException(); } }}
            onBlur={() => { commitException(); if (!exceptions.length && !exInput.trim()) setExOpen(false); }} />
        </div>
      )}

      <PreviewStrip preview={preview} />

      <div className="cc-rule-sentence" ref={kindRef}>
        <span className="cc-rule-word"><Icon name="chevron-inline" size={12} className="cc-rule-arrow" />סווג כ</span>
        <div className="cc-kind-seg" role="group" aria-label="סווג כ">
          {LINE_KINDS.map((k) => (
            <button key={k} type="button" className="cc-kind-option" aria-pressed={lineKind === k}
              onClick={() => { setLineKind(k); setErrors((x) => ({ ...x, kind: undefined })); }}>
              {LINE_KIND_LABELS[k]}
            </button>
          ))}
        </div>
        {lineKind === LINE_KIND_ADJUSTMENT && (
          <select className="form-control cc-rule-select" aria-label="סוג" value={adjType} onChange={(e) => setAdjType(e.target.value)}>
            <option value="">ללא סוג</option>
            {ADJUSTMENT_TYPES.map((t) => <option key={t} value={t}>{ADJUSTMENT_TYPE_LABELS[t]}</option>)}
          </select>
        )}
      </div>
      {errors.kind && <div className="cc-rule-error" role="alert">{errors.kind}</div>}

      {errors.server && <div className="cc-rule-error" role="alert">{errors.server}</div>}
      <div className="cc-rule-actions">
        <button type="button" className="btn btn-primary" disabled={saving || duplicate} onClick={save}>שמור כלל</button>
        <button type="button" className="btn btn-secondary" disabled={saving} onClick={onCancel}>ביטול</button>
      </div>
    </section>
  );
}

function RuleRow({ rule, pending, onToggle, onDelete }) {
  const created = rule.created_at ? formatIsoDayShort(String(rule.created_at).slice(0, 10)) : '';
  const meta = [
    rule.exceptions.length ? `חוץ מ־ ${rule.exceptions.join(', ')}` : null,
    countCopy(rule.applied_count),
    rule.active ? null : 'כבוי',
    created ? `נוצר ${created}` : null,
  ].filter(Boolean).join(' · ');
  return (
    <li className={`cc-rule-item${rule.active ? '' : ' is-off'}`}>
      <div className="cc-rule-main">
        <div className="cc-rule-line">
          {phraseOf(rule.match_type)} <bdi><strong>"{rule.pattern}"</strong></bdi>
          <Icon name="chevron-inline" size={12} className="cc-rule-arrow" /><span className="sr-only">סווג כ </span>
          {lineKindLabel(rule.line_kind, rule.adjustment_type)}
        </div>
        <div className="cc-muted">{meta}</div>
      </div>
      <div className="cc-rule-controls">
        <Switch checked={rule.active} disabled={pending} onChange={(on) => onToggle(rule, on)} label={`הכלל "${rule.pattern}" פעיל`} />
        <button type="button" className="btn btn-sm btn-secondary" aria-label={`מחק כלל ${rule.pattern}`}
          onClick={() => onDelete(rule)}><Icon name="trash" size={14} /></button>
      </div>
    </li>
  );
}

export default function LineKindRulesView({ onCount }) {
  const [rules, setRules] = useState(null);
  const [error, setError] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [askConfirm, confirmDialog] = useConfirm();
  // Rule ids with a PATCH in flight (ref = the synchronous guard, state = the
  // disabled switch).
  const inFlight = useRef(new Set());
  const [pendingIds, setPendingIds] = useState(() => new Set());
  // A logical clock: `changedAt` holds, per rule, when its switch request last
  // finished. A list read keeps the page's own `active` for a rule whose
  // request is still in flight or finished after that read STARTED — the
  // server answered it from before the change (Codex final review, PR #302:
  // a reload after deleting another rule turned a pending "on" back to off).
  const clock = useRef(0);
  const changedAt = useRef(new Map());

  const load = useCallback(async () => {
    setError(false);
    const startedAt = clock.current;
    try {
      const rows = (await commandCenterApi.getLineKindRules()).data?.rows || [];
      const ours = (id) => inFlight.current.has(id) || (changedAt.current.get(id) ?? -1) > startedAt;
      setRules((prev) => rows.map((r) => {
        const mine = ours(r.id) && prev?.find((p) => p.id === r.id);
        return mine ? { ...r, active: mine.active } : r;
      }));
      onCount?.(rows.length);
    } catch {
      setError(true);
      setRules(null);
    }
  }, [onCount]);
  useEffect(() => { load(); }, [load]);
  // No rules yet: the form is the page.
  useEffect(() => { if (rules && rules.length === 0) setFormOpen(true); }, [rules]);

  // Optimistic: the switch moves at once, and moves back if the server says no.
  // One request per rule at a time — the switch is disabled until it answers —
  // so a slow "on" can never land after a later "off" and leave the page
  // showing off while new lines are classified (Codex re-review, PR #302).
  const setPending = (id, on) => {
    if (on) inFlight.current.add(id); else inFlight.current.delete(id);
    setPendingIds(new Set(inFlight.current));
  };
  const setActive = (id, active) => setRules((list) => list && list.map((r) => (r.id === id ? { ...r, active } : r)));
  const toggle = async (rule, active) => {
    if (inFlight.current.has(rule.id)) return;
    setPending(rule.id, true);
    clock.current += 1;
    setActive(rule.id, active);
    // Whatever happens, the page ends showing what the server now holds.
    let final = active;
    try {
      await commandCenterApi.setLineKindRuleActive(rule.id, active);
    } catch (err) {
      final = !active;
      toast(err?.response?.data?.error || 'עדכון הכלל נכשל', 'error');
    } finally {
      clock.current += 1;
      changedAt.current.set(rule.id, clock.current);
      setActive(rule.id, final);
      setPending(rule.id, false);
    }
  };
  const remove = (rule) => askConfirm({
    title: 'מחיקת כלל',
    message: `למחוק את הכלל "${rule.pattern}"?`,
    detail: 'שורות שהכלל כבר סיווג נשארות מסווגות. שורות חדשות לא יסווגו לפיו.',
    confirmLabel: 'מחק',
    tone: 'danger',
    onConfirm: async () => {
      try {
        await commandCenterApi.deleteLineKindRule(rule.id);
        toast('הכלל נמחק');
        await load();
      } catch (err) {
        toast(err?.response?.data?.error || 'מחיקת הכלל נכשלה', 'error');
      }
    },
  });

  const fromClassified = async () => {
    setMenuOpen(false);
    let plan;
    try {
      plan = (await commandCenterApi.lineKindRulesFromClassified(false)).data;
    } catch (err) {
      toast(err?.response?.data?.error || 'הבדיקה נכשלה — אפשר לנסות שוב', 'error');
      return;
    }
    if (!plan.createCount && plan.partial) {
      toast('הבדיקה נעצרה מוקדם ולא מצאה כללים חדשים בסיווגים האחרונים — אפשר לנסות שוב');
      return;
    }
    if (!plan.createCount) {
      toast(plan.conflictCount
        ? `אין כללים חדשים ליצור · ${n(plan.conflictCount)} טקסטים סווגו ביותר מדרך אחת`
        : 'אין כללים חדשים ליצור');
      return;
    }
    const notes = [];
    if (plan.conflictCount) {
      notes.push(`${n(plan.conflictCount)} טקסטים סווגו ביותר מדרך אחת ולא יקבלו כלל: ${plan.conflicts.slice(0, 5).map((c) => c.pattern).join(', ')}${plan.conflictCount > 5 ? '…' : ''}`);
    }
    if (plan.partial) notes.push('הבדיקה נעצרה מוקדם — הכללים נבנו רק מהסיווגים האחרונים.');
    if (plan.more) notes.push(`עוד ${n(plan.more)} טקסטים יחכו ללחיצה הבאה (עד 1,000 כללים בכל פעם).`);
    askConfirm({
      title: 'יצירת כללים מהסיווגים',
      message: `ייווצרו ${n(plan.createCount)} כללים מסוג "זהה בדיוק", אחד לכל טקסט שסיווגת ביד.`,
      detail: notes.length ? notes.join(' ') : 'הכללים יחולו על שורות חדשות בלבד.',
      confirmLabel: 'צור כללים',
      onConfirm: async () => {
        try {
          const r = (await commandCenterApi.lineKindRulesFromClassified(true)).data;
          toast(`נוצרו ${n(r.created)} כללים`);
          await load();
        } catch (err) {
          toast(err?.response?.data?.error || 'יצירת הכללים נכשלה', 'error');
        }
      },
    });
  };

  const shown = useMemo(() => {
    const q = normText(search);
    if (!rules || !q) return rules;
    return rules.filter((r) => normText(r.pattern).includes(q) || r.exceptions.some((e) => normText(e).includes(q)));
  }, [rules, search]);

  return (
    <section className="cc-card" aria-label="כללי סיווג">
      <div className="cc-card-head">
        <strong>כללי סיווג{rules ? ` (${rules.length})` : ''}</strong>
        <span className="cc-spacer" />
        {!formOpen && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setFormOpen(true)}>
            <Icon name="plus" size={14} /> כלל חדש
          </button>
        )}
        <span className="cc-rowmenu">
          <button type="button" className="cc-rowmenu-btn" aria-label="עוד פעולות" aria-haspopup="menu"
            aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>⋮</button>
          {menuOpen && (
            <>
              <div className="cc-rowmenu-overlay" onClick={() => setMenuOpen(false)} />
              <div className="cc-rowmenu-panel" role="menu">
                <button type="button" role="menuitem" className="cc-rowmenu-item" onClick={fromClassified}>
                  צור כללים מכל מה שסיווגתי
                </button>
              </div>
            </>
          )}
        </span>
      </div>
      <p className="cc-muted cc-rules-sub">כלל מסווג אוטומטית כל שורה חדשה שתואמת לו. שורות קיימות לא משתנות.</p>

      {formOpen && rules && <RuleForm rules={rules} onSaved={load} onCancel={() => setFormOpen(false)} />}

      <LoadState loading={rules === null && !error} error={error} onRetry={load}
        empty={rules?.length === 0}
        emptyMessage={(
          <div className="cc-rules-empty">
            <div>עוד אין כללים. כתבו כלל ראשון למעלה, או צרו כללים מכל השורות שכבר סיווגתם ביד.</div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={fromClassified}>צור כללים מכל מה שסיווגתי</button>
          </div>
        )}>
        {rules && rules.length > SEARCH_FROM && (
          <label className="cc-search-field cc-rules-search">
            <Icon name="search" size={15} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="חיפוש בכללים…" aria-label="חיפוש בכללים" />
          </label>
        )}
        {shown && shown.length === 0 ? (
          <div className="cc-empty">אין כללים שמתאימים לחיפוש</div>
        ) : (
          <ul className="cc-rule-list">
            {shown?.map((r) => <RuleRow key={r.id} rule={r} pending={pendingIds.has(r.id)} onToggle={toggle} onDelete={remove} />)}
          </ul>
        )}
      </LoadState>
      {confirmDialog}
    </section>
  );
}
